import React, { useState, useEffect } from 'react';
import { Search, Upload, FileText, TrendingUp, Menu, X, Briefcase, Users, BarChart3 } from 'lucide-react';
import { health, statistiche, getUtentiAttivi } from './api';
import Ricerca from './components/Ricerca';
import Documenti from './components/Documenti';
import Carica from './components/Carica';
import Finanziario from './components/Finanziario';
import SchedaRicorso from './components/SchedaRicorso';
import Clienti from './components/Clienti';
import TipiRicorso from './components/TipiRicorso';
import Tribunali from './components/Tribunali';
import Report from './components/Report';
import Utenti from './components/Utenti';
import AuditLog from './components/AuditLog';
import RuoliPermessi from './components/RuoliPermessi';
import Deploy from './components/Deploy';
import Campagne from './components/Campagne';
import Login from './components/Login';
import { useAuth } from './AuthContext';
import { LogOut, UserCog, ShieldCheck, Settings, ChevronDown, ChevronRight, Landmark, Rocket, Megaphone } from 'lucide-react';
import './App.css';

export default function App() {
  const { utente, logout, haPermesso } = useAuth();
  // ?pagina=campagne riapre il report Campagne con il periodo dell'URL
  const [pagina, setPagina] = useState(() =>
    new URLSearchParams(window.location.search).get('pagina') === 'campagne' ? 'campagne' : 'ricerca');
  const [stats, setStats] = useState(null);
  const [sistemaOk, setSistemaOk] = useState(null);
  const [menuAperto, setMenuAperto] = useState(false);
  const [configAperto, setConfigAperto] = useState(false);
  const [utentiAttivi, setUtentiAttivi] = useState(null);

  useEffect(() => {
    if (!utente) return undefined;
    const leggi = () => getUtentiAttivi().then(r => setUtentiAttivi(r.data)).catch(() => {});
    leggi();
    const t = setInterval(leggi, 60000);
    return () => clearInterval(t);
  }, [utente]);

  useEffect(() => {
    if (!utente) return;  // niente chiamate finché non autenticato
    health().then(r => setSistemaOk(
      Object.values(r.data).every(v => v === 'ok')
    )).catch(() => setSistemaOk(false));
    statistiche().then(r => setStats(r.data)).catch(() => {});
  }, [utente]);

  const tutteVoci = [
    { id: 'ricerca', label: 'Ricerca AI', icon: Search, permesso: 'ai.usa' },
    { id: 'scheda', label: 'Ricorsi', icon: Briefcase, permesso: 'ricorsi.leggi' },
    { id: 'clienti', label: 'Clienti', icon: Users, permesso: 'clienti.leggi' },
    { id: 'documenti', label: 'Documenti', icon: FileText, permesso: 'documenti.leggi' },
    { id: 'carica', label: 'Carica', icon: Upload, permesso: 'documenti.carica' },
    { id: 'finanziario', label: 'Finanziario', icon: TrendingUp, permesso: 'finanziario.leggi' },
    { id: 'report', label: 'Report', icon: BarChart3, permesso: 'report.leggi' },
    { id: 'campagne', label: 'Campagne', icon: Megaphone, permesso: 'campagne.leggi' },
    { id: 'audit', label: 'Audit', icon: ShieldCheck, permesso: 'audit.leggi' },
    { id: 'deploy', label: 'Deploy', icon: Rocket, permesso: 'deploy.esegui', soloStaging: true },
    // gruppo Configurazione
    { id: 'tribunali', label: 'Gestione tribunali', icon: Landmark, permesso: 'configurazione.gestisci', gruppo: 'config' },
    { id: 'tipi', label: 'Tipi ricorso', icon: Briefcase, permesso: 'configurazione.gestisci', gruppo: 'config' },
    { id: 'utenti', label: 'Utenti', icon: UserCog, permesso: 'utenti.gestisci', gruppo: 'config' },
    { id: 'ruoli', label: 'Ruoli', icon: ShieldCheck, permesso: 'utenti.gestisci', gruppo: 'config' },
  ];
  const voci = tutteVoci.filter(v => (!v.permesso || haPermesso(v.permesso)) && (!v.soloStaging || utente?.ambiente === 'staging'));
  const etichettaPagina = tutteVoci.find(v => v.id === pagina)?.label;
  useEffect(() => {
    const prefisso = window.location.hostname.startsWith('staging') ? '[TEST] ' : '';
    document.title = prefisso + (utente && etichettaPagina && pagina !== 'ricerca' ? `${etichettaPagina} · Studio Marone` : 'Studio Marone · Sistema AI');
  }, [etichettaPagina, utente, pagina]);
  const vociPrincipali = voci.filter(v => !v.gruppo);
  const vociConfig = voci.filter(v => v.gruppo === 'config');
  const configAttiva = vociConfig.some(v => v.id === pagina);
  const configEspansa = configAperto || configAttiva;

  if (!utente) {
    return <Login />;
  }

  return (
    <div className="app">
      <aside className={`sidebar ${menuAperto ? 'aperta' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">⚖️</span>
            <div>
              <div className="logo-titolo">Studio Marone</div>
              <div className="logo-sub">Sistema AI</div>
            </div>
          </div>
          <button className="menu-close" onClick={() => setMenuAperto(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="nav">
          {vociPrincipali.map(v => (
            <button key={v.id}
              className={`nav-item ${pagina === v.id ? 'attivo' : ''}`}
              onClick={() => { setPagina(v.id); setMenuAperto(false); }}>
              <v.icon size={18} />
              <span>{v.label}</span>
            </button>
          ))}
          {vociConfig.length > 0 && (
            <>
              <button className="nav-item"
                onClick={() => setConfigAperto(a => !a)}
                style={{ opacity: configAttiva ? 1 : 0.85 }}>
                <Settings size={18} />
                <span style={{ flex: 1 }}>Configurazione</span>
                {configEspansa ? <ChevronDown size={14} style={{ opacity: 0.6 }} /> : <ChevronRight size={14} style={{ opacity: 0.6 }} />}
              </button>
              {configEspansa && vociConfig.map(v => (
                <button key={v.id}
                  className={`nav-item ${pagina === v.id ? 'attivo' : ''}`}
                  style={{ paddingLeft: 36, fontSize: 13 }}
                  onClick={() => { setPagina(v.id); setMenuAperto(false); }}>
                  <v.icon size={16} />
                  <span>{v.label}</span>
                </button>
              ))}
            </>
          )}
        </nav>

        <div className="stato-sistema" style={{borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 10}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
            <div>
              <div style={{fontSize: 13, fontWeight: 600}}>{utente.nome} {utente.cognome}</div>
              <div style={{fontSize: 11, opacity: 0.6, textTransform: 'capitalize'}}>{utente.ruolo}</div>
            </div>
            <button onClick={logout} title="Esci" style={{background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', opacity: 0.7, padding: 4}}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
        <div className="stato-sistema">
          <div className={`stato-dot ${sistemaOk === null ? 'carico' : sistemaOk ? 'ok' : 'errore'}`} />
          <span>{sistemaOk === null ? 'Connessione...' : sistemaOk ? 'Sistema operativo' : 'Sistema non raggiungibile'}</span>
        </div>

        {stats && (
          <div className="stats-box">
            <div className="stat-item">
              <span className="stat-n">{stats.documenti_indicizzati}</span>
              <span className="stat-l">Documenti</span>
            </div>
            <div className="stat-item">
              <span className="stat-n">{stats.vettori_qdrant}</span>
              <span className="stat-l">Vettori</span>
            </div>
            {utentiAttivi && (
              <div className="stat-item"
                title={utentiAttivi.utenti ? utentiAttivi.utenti.map(u => `${u.nome} ${u.cognome}`).join(', ') : 'Utenti con l\'app aperta negli ultimi 2 minuti'}>
                <span className="stat-n">{utentiAttivi.attivi}</span>
                <span className="stat-l">Utenti attivi</span>
              </div>
            )}
          </div>
        )}
      </aside>

      <main className="main">
        <div className="topbar">
          <button className="menu-toggle" onClick={() => setMenuAperto(true)}>
            <Menu size={22} />
          </button>
          <h1 className="titolo-pagina">
            {voci.find(v => v.id === pagina)?.label}
          </h1>
        </div>

        <div className="contenuto">
          {pagina === 'ricerca' && <Ricerca />}
          {pagina === 'scheda' && <SchedaRicorso />}
          {pagina === 'tipi' && <TipiRicorso />}
          {pagina === 'tribunali' && <Tribunali />}
          {pagina === 'clienti' && <Clienti />}
          {pagina === 'documenti' && <Documenti />}
          {pagina === 'carica' && <Carica />}
          {pagina === 'finanziario' && <Finanziario />}
          {pagina === 'report' && <Report />}
          {pagina === 'utenti' && <Utenti />}
          {pagina === 'ruoli' && <RuoliPermessi />}
          {pagina === 'audit' && <AuditLog />}
          {pagina === 'deploy' && <Deploy />}
          {pagina === 'campagne' && haPermesso('campagne.leggi') && <Campagne />}
        </div>
      </main>
    </div>
  );
}
